import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, TrendingUp, Plus, Loader2, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Progress = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ weight: '', date: new Date().toISOString().split('T')[0], notes: '' });

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        toast.error('Bitte absolviere zuerst das Quiz');
        navigate('/quiz');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/progress/${userId}`);
      setProgress(response.data.progress || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Fehler beim Laden der Fortschritte');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgress = async () => {
    if (!newEntry.weight) {
      toast.error('Bitte gib dein Gewicht ein');
      return;
    }

    try {
      const userId = localStorage.getItem('userId');
      await axios.post(`${BACKEND_URL}/api/progress`, {
        user_id: userId,
        weight: parseFloat(newEntry.weight),
        date: new Date(newEntry.date).toISOString(),
        notes: newEntry.notes
      });

      toast.success('Fortschritt hinzugefügt!');
      setDialogOpen(false);
      setNewEntry({ weight: '', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchProgress();
    } catch (error) {
      console.error('Error adding progress:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const chartData = progress.map(p => ({
    date: new Date(p.date).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }),
    weight: p.weight
  })).reverse();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="h-10 px-4 rounded-lg hover:bg-zinc-800"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Dashboard
            </Button>
            <h1 className="text-xl font-bold">Fortschritte</h1>
            <div className="w-24" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-5xl py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
              <TrendingUp className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Deine Fortschritte</h2>
              <p className="text-base text-zinc-400 mt-1">Verfolge deine Transformation</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-progress-btn"
                className="h-12 px-6 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Eintrag hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle>Neuer Fortschritt</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="weight">Gewicht (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder="z.B. 62.5"
                    value={newEntry.weight}
                    onChange={(e) => setNewEntry({ ...newEntry, weight: e.target.value })}
                    className="mt-2 h-12 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Datum</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                    className="mt-2 h-12 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notizen (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="z.B. Fühle mich gut!"
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                    className="mt-2 h-12 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <Button
                  data-testid="save-progress-btn"
                  onClick={handleAddProgress}
                  className="w-full h-12 rounded-lg bg-purple-600 hover:bg-purple-500"
                >
                  Speichern
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Chart */}
        {chartData.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6 mb-8"
          >
            <h3 className="text-xl font-semibold mb-6">Gewichtsverlauf</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }}
                  labelStyle={{ color: '#a1a1aa' }}
                />
                <Line type="monotone" dataKey="weight" stroke="#a855f7" strokeWidth={3} dot={{ fill: '#a855f7', r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-12 mb-8 text-center"
          >
            <TrendingUp className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Noch keine Einträge</h3>
            <p className="text-zinc-400 mb-6">Füge deinen ersten Fortschritt hinzu, um deine Transformation zu verfolgen</p>
          </motion.div>
        )}

        {/* Progress List */}
        {progress.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold mb-4">Verlauf</h3>
            <div className="space-y-3">
              {progress.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{entry.weight} kg</div>
                      <div className="text-sm text-zinc-400">
                        {new Date(entry.date).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      {entry.notes && <div className="text-sm text-zinc-500 mt-1">{entry.notes}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Progress;